import { type KeyboardEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ForgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email is required').pipe(z.email('Enter a valid email')),
});

export type ForgotPasswordValues = z.infer<typeof ForgotPasswordSchema>;

interface ForgotPasswordRequestStepProps {
  readonly defaultEmail?: string;
  readonly onSubmit: (values: ForgotPasswordValues) => Promise<void>;
  readonly topError?: string | null;
}

export function ForgotPasswordRequestStep({
  defaultEmail = '',
  onSubmit,
  topError,
}: ForgotPasswordRequestStepProps) {
  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: { email: defaultEmail },
  });

  const submit = form.handleSubmit(async (values) => {
    await onSubmit(values);
  });

  // No <form> elements per apps/web/CLAUDE.md §6 — wire Enter manually.
  const submitOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      void submit();
    }
  };

  const emailError = form.formState.errors.email?.message;
  const isSubmitting = form.formState.isSubmitting;

  return (
    <div className="space-y-4">
      {topError ? (
        <Alert variant="destructive">
          <AlertDescription>{topError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="forgot-email">Email</Label>
        <Input
          id="forgot-email"
          type="email"
          autoComplete="email"
          aria-invalid={Boolean(emailError)}
          aria-describedby={emailError ? 'forgot-email-error' : undefined}
          onKeyDown={submitOnEnter}
          {...form.register('email')}
        />
        {emailError ? (
          <p id="forgot-email-error" className="text-destructive text-xs">
            {emailError}
          </p>
        ) : null}
        <p className="text-muted-foreground text-xs">
          We&apos;ll email a 6-digit code you can use to set a new password.
        </p>
      </div>

      <Button
        type="button"
        className="w-full"
        disabled={isSubmitting}
        onClick={() => void submit()}
      >
        {isSubmitting ? 'Sending code…' : 'Send reset code'}
      </Button>
    </div>
  );
}
